/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { Conversation, ResearchConversation, Transcript } from '@repo/common';
import type { ConversationModel } from '@repo/common/types/Conversation';
import { transcriptToConversation } from '@repo/toolslib/src/utils/calendar';
import { uuidV4 } from '@repo/toolslib/src/utils/id.util';
import { derived, get, writable, type Readable } from 'svelte/store';
import { getHandler } from '../sharedlib';
import { waitForAuthAndUserIdFast } from './waitForAuth';

export interface ConversationStore {
  conversations: Map<string, Conversation>;
  messageCounts: Map<string, number>;
  loading: boolean;
  error: string | null;
  lastSync: Date | null;
}

const createConversationStore = () => {
  const internalStore = writable<ConversationStore>({
    conversations: new Map(),
    messageCounts: new Map(),
    loading: false,
    error: null,
    lastSync: null
  });

  const { subscribe, update } = internalStore;

  let conversationModel: ConversationModel | null = null;
  let syncInProgress = false;

  const initializeConversationModel = async () => {
    if (!conversationModel) {
      try {
        const handler = getHandler();
        conversationModel = await handler.getConversationModel();
      } catch (err) {
        console.warn('Failed to initialize conversation model:', err);
        throw err;
      }
    }
    return conversationModel;
  };

  const refreshConversations = async () => {
    if (syncInProgress) {
      console.log('[refreshConversations] Conversation sync already in progress, skipping...');
      return;
    }
    console.log('[refreshConversations] Starting refresh...');
    await waitForAuthAndUserIdFast();

    try {
      syncInProgress = true;
      update((store) => ({ ...store, loading: true, error: null }));

      const { conversationModel, messageModel } = await getHandler().getDatabaseModels();

      // Get all active conversations, ordered by most recent
      const allConversations = await conversationModel.findAllObj();

      const messageCounts = new Map<string, number>();

      const result = await messageModel.groupByConversation();
      for (const conv of result as any[]) {
        messageCounts.set(conv.id, conv.messageCount);
      }

      update((store) => ({
        ...store,
        conversations: new Map(allConversations.map((conv: Conversation) => [conv.id, conv])),
        messageCounts,
        loading: false,
        lastSync: new Date()
      }));

      console.log(`Loaded ${allConversations.length} conversations`);
    } catch (err: any) {
      if (err.message.includes('User ID not set')) {
        console.log('User ID not set');
        return;
      }
      console.warn('Failed to refresh conversations:', err);
      update((store) => ({
        ...store,
        loading: false,
        error: err.message
      }));
    } finally {
      syncInProgress = false;
    }
  };

  const getConversation = async (id: string): Promise<Conversation | null> => {
    try {
      await waitForAuthAndUserIdFast();
      const model = await initializeConversationModel();
      if (!model) {
        console.warn('Conversation model not initialized');
        return null;
      }
      const userId = getHandler().getUserId();
      if (!userId) {
        throw new Error('User ID not set');
      }
      const results = await model.findAllObj({ where: { id } });
      const conversation = results.length > 0 ? { ...results[0], userId } : null;
      return conversation;
    } catch (err: any) {
      console.warn('Failed to get conversation:', err);
      return null;
    }
  };

  const ensureConversation = async (
    conversationId: string,
    options?: { type?: string; name?: string }
  ): Promise<Conversation | null> => {
    if (typeof conversationId !== 'string' || conversationId.length > 100) {
      console.warn(`Conversation ID is required, got:`, conversationId);
      throw new Error(`Conversation ID is required`);
    }
    try {
      console.log('[conversationStore] ensureConversation called for:', conversationId);
      const existingConversation = await getConversation(conversationId);
      if (existingConversation) {
        console.log('[conversationStore] Found existing conversation:', conversationId);
        return existingConversation;
      }

      const model = await initializeConversationModel();
      if (!model) {
        throw new Error('Conversation model not initialized');
      }
      const userId = getHandler().getUserId();
      if (!userId) {
        console.error('[conversationStore] User ID not set in ensureConversation');
        throw new Error('User ID not set');
      }

      // Use provided type/name or defaults
      const conversationType = options?.type || 'research';
      const conversationName = options?.name || 'Current draft';

      console.log('[conversationStore] Creating new conversation:', {
        conversationId,
        conversationType,
        userId
      });

      await model.bulkUpsertItems([
        {
          id: conversationId,
          name: conversationName,
          type: conversationType,
          createdAt: new Date(),
          updatedAt: new Date(),
          userId: userId
        }
      ]);

      // Create the ResearchConversation object
      const conversation: Conversation = {
        id: conversationId,
        type: conversationType,
        name: conversationName,
        conversationId: conversationId,
        nodes: [],
        messageCount: 0,
        date: new Date(),
        query: conversationName,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: userId
      };

      // Update store
      setConversation(conversation);
      return conversation;
    } catch (err: any) {
      console.error('[conversationStore] Failed to ensure conversation:', err);
      return null;
    }
  };

  const ensureConversationByTranscript = async (
    transcript: Transcript
  ): Promise<Conversation | null> => {
    try {
      const existingConversation = await getConversation(transcript.id);
      if (existingConversation) {
        return existingConversation;
      }

      const model = await initializeConversationModel();
      if (!model) {
        throw new Error('Conversation model not initialized');
      }
      const conversation = transcriptToConversation(transcript);
      if (!conversation) {
        throw new Error('Failed to convert transcript to conversation');
      }
      await model.bulkUpsertItems([conversation]);

      // Update store
      setConversation(conversation);
      return conversation;
    } catch (err: any) {
      console.warn('Failed to ensure conversation:', err);
      return null;
    }
  };

  const createResearchConversation = async (query: string): Promise<ResearchConversation> => {
    try {
      const model = await initializeConversationModel();
      if (!model) {
        throw new Error('Conversation model not initialized');
      }

      // Create the conversation in the database
      const conversationId = uuidV4();

      const userId = getHandler().getUserId();
      if (!userId) {
        throw new Error('User ID not set');
      }

      const conversationName = query.slice(0, 100) || 'New Research';

      await model.bulkCreateItems([
        {
          id: conversationId,
          name: conversationName,
          type: 'research',
          createdAt: new Date(),
          updatedAt: new Date(),
          userId,
          data: {
            query: query,
            autoGenerated: false
          }
        }
      ]);

      // Create the ResearchConversation object with the same ID
      const conversation: ResearchConversation & { name: string } = {
        id: conversationId,
        type: 'research',
        name: conversationName,
        conversationId: conversationId,
        nodes: [],
        messageCount: 0,
        date: new Date(),
        query,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Update the local store
      setConversation(conversation as unknown as Conversation);

      return conversation;
    } catch (err: any) {
      console.warn('Failed to create research conversation:', err);

      // Fallback to local-only creation
      const id = uuidV4();
      const conversation: ResearchConversation = {
        id,
        type: 'research',
        conversationId: id,
        nodes: [],
        messageCount: 0,
        date: new Date(),
        query,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      return conversation;
    }
  };

  const deleteConversation = async (conversationId: string): Promise<boolean> => {
    try {
      const model = await initializeConversationModel();
      await (model as any).destroyRecords?.({ id: conversationId });

      // Update store immediately
      removeConversation(conversationId);

      return true;
    } catch (err: any) {
      console.warn('Failed to delete conversation:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const updateConversation = async (
    conversationId: string,
    updates: Partial<Pick<Conversation, 'name' | 'type' | 'data'>>
  ): Promise<boolean> => {
    try {
      const model = await initializeConversationModel();
      await (model as any).updateRecord?.({ id: conversationId }, updates);

      // Update local store
      update((store) => {
        const conversation = store.conversations.get(conversationId);
        if (!conversation) return store;

        const updatedConversation = {
          ...conversation,
          ...updates,
          updatedAt: new Date()
        };

        const newConversations = new Map(store.conversations);
        newConversations.set(conversationId, updatedConversation);
        return { ...store, conversations: newConversations };
      });

      return true;
    } catch (err: any) {
      console.warn('Failed to update conversation:', err);
      return false;
    }
  };

  // Helper methods for conversation management
  const getConversationFromStore = (conversationId: string): Conversation | undefined => {
    return get(internalStore).conversations.get(conversationId);
  };

  const setConversation = (conversation: Conversation) => {
    update((store) => {
      const newConversations = new Map(store.conversations);
      newConversations.set(conversation.id, conversation);
      return { ...store, conversations: newConversations };
    });
  };

  const removeConversation = (conversationId: string) => {
    update((store) => {
      const newConversations = new Map(store.conversations);
      newConversations.delete(conversationId);
      return { ...store, conversations: newConversations };
    });
  };

  // Derived stores for different conversation types - created once and cached
  const researchConversationsStore = derived({ subscribe }, ($store) => {
    const conversations = Array.from($store.conversations.values())
      .filter(
        (conv) =>
          conv.type === 'research' &&
          !conv.deletedAt &&
          // Filter out business plan conversations (identified by prefix)
          !(
            (conv.data as any)?.query?.startsWith('[BUSINESS_PLAN]') ||
            conv.name?.startsWith('[BUSINESS_PLAN]')
          )
      )
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    // Map to ResearchConversation format with message count from store
    return conversations.map(
      (conv): ResearchConversation => ({
        id: conv.id,
        type: 'research',
        conversationId: conv.id,
        nodes: [],
        messageCount: $store.messageCounts.get(conv.id) || 0,
        date: conv.updatedAt,
        query: (conv.data as any)?.query || conv.name,
        createdAt: conv.createdAt,
        updatedAt: conv.updatedAt
      })
    );
  });

  const allConversationsStore = derived({ subscribe }, ($store) => {
    return Array.from($store.conversations.values())
      .filter((conv) => !conv.deletedAt)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  });

  // Getter functions that return the cached derived stores
  const getResearchConversations = (): Readable<ResearchConversation[]> => {
    return researchConversationsStore;
  };

  const getAllConversations = (): Readable<Conversation[]> => {
    return allConversationsStore;
  };

  return {
    subscribe,
    refreshConversations,
    getConversation,
    ensureConversation,
    ensureConversationByTranscript,
    createResearchConversation,
    deleteConversation,
    updateConversation,
    getConversationFromStore,
    setConversation,
    removeConversation,
    getResearchConversations,
    getAllConversations
  };
};

export const conversationStore = createConversationStore();

// Create a derived store that maintains the old interface for backward compatibility
// Use the getter to get the cached derived store (not create a new one each time)
export const researchStore = derived(
  conversationStore.getResearchConversations(),
  ($conversations) => ({
    list: $conversations
  })
);

// Export individual functions for backward compatibility
export const {
  refreshConversations: refreshResearchItems,
  getConversation,
  ensureConversation,
  ensureConversationByTranscript,
  createResearchConversation,
  deleteConversation: deleteResearchConversation,
  updateConversation
} = conversationStore;

// Legacy function stubs for backward compatibility
export const loadStoredResearchItems = async () => {
  // This was used to load from file system, now we load directly from DB
  return conversationStore.refreshConversations();
};
